import ReactDOM from 'react-dom'
import {
  DEFAULT_IMAGE,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  SEARCH_TYPES_WITHOUT_TEXT,
  SEARCH_QUERY,
  TYPES_WITH_FILTER,
  MOBILE_AGENTS,
  PROFESSIONALS,
  IMAGES,
  ARTICLE_ARCHIVE
} from '../utils/constants'
import { LEVEL_CATEGORIES_TYPES, EXPERTISE, EXP_1,
  FILTERS_KEYS_ARR, CATEGORIES, OFFICES, TOPIC } from '../components/filters/constants'
import { searchOffices } from './../utils/requests'
import { tagFiltersMessages } from '../hoc/messages'

/**
 * Send Google Analitycs and Insights PageView metrics.
*/
export const trackingPageViews = () => {
  window.ga('set', 'page', window.location.pathname + window.location.search)
  window.ga('send', 'pageview')
  window.adInsights.p()
}

/**
 * Send Google Analytics Events.
 * @param {string} gaEventCategory:  The object that was interacted with (e.g. 'Video').
 * @param {string} gaEventAction:  The type of interaction (e.g. 'play').
 * @param {string} gaEventLabel:  Useful for categorizing events (e.g. 'Fall Campaign') - Optional.
 * @param {number} gaEventValue:  A numeric value associated with the event (e.g. 42) - Optional.
*/
export const sendGaEventTracking = (gaEventCategory, gaEventAction, gaEventLabel, gaEventValue) => {
  if (window.ga && window.ga.loaded) {
    window.ga('send', {
      hitType: 'event',
      eventCategory: gaEventCategory,
      eventAction: gaEventAction,
      ...(gaEventLabel && { eventLabel: gaEventLabel }),
      ...(gaEventValue && { eventValue: gaEventValue })
    })
  }
}

/**
 * Receives a default src href image to be returned, if not returns a default href image
 * @param   {string} src - href image
 * @param   {string} [defaultImage=DEFAULT_IMAGE] - image placeholder url
 *
 * @returns {string} src param if exists else DEFAULT_IMAGE
*/
export const getImageOrDefaultSrc = (src, defaultImage = DEFAULT_IMAGE) => src || defaultImage

/**
 * Format an url to track the link with Google Analitycs params ad_source and ad_medium
 * @param   {string} url - url of a document
 * @param   {string} type - current type of search
 *
 * @returns {string} a url of a document formatted with Google Analitycs track params
*/
export const urlWithTrack = (url = '', type = '') => {
  const urlParamKey = url.split('?').length > 1 ? '&' : '?'
  return `${url}${urlParamKey}ad_source=search&ad_medium=search_result_${type}`
}

/**
 * Compare both parameters taking care of case sensitive, formatting the output string and returning it
 * @param   {array}  matches - collection of coincidences of the typed query with the item to filter
 * @param   {string} name - name to filter
 *
 * @returns {string} the name formatted with <em> tags where the matchKey coincided with a key from the name
*/
export const getFilteredName = (matches, name) => {
  if (matches) {
    return [...new Set(matches)].reduce((acc, curr) => {
      const matchKey = regexCreator(`${curr}(?!m>)`, 'g')
      acc = acc.replace(matchKey, `<em>${curr}</em>`)
      return acc
    }, name)
  }
}

/**
 * Takes a query to filter through an array of objects by name
 * @param   {string} query - search input
 * @param   {array}  arr - array of objects to filter
 *
 * @returns {array}  an array of object filtered by query parameter
 */
export const filterizer = query => arr => {
  if (query.includes('.')) {
    return []
  } else if (query) {
    const regex = regexCreator(query)
    return arr.filter(({ name }) => name && name.match(regex))
      .map(item => {
        const matches = item.name.match(regex)
        const filteredName = getFilteredName(matches, item.name)
        return { ...item, name: item.name, filteredName }
      })
  }
  return arr
}

/**
 * Creates a regex depending of the text and options parameters
 * @param   {string}           pattern - text to create the regex
 * @param   {string}           [flags='gi'] - regex options
 *
 * @returns {(object|boolean)} a regex with the pattern and flags received if are valid, else returns false
*/
export const regexCreator = (pattern, flags = 'gi') => {
  try {
    return new RegExp(`${pattern}`, flags)
  } catch {
    return false
  }
}

/**
 * Sort an array of objects depending of parameters received
 * @param   {array}   arr - array of objects to sort
 * @param   {string}  field - string key that will be used to order
 * @param   {boolean} descending - if true is decending order
 * @param   {boolean} stringNumbers - if true should be able to order strings that can coerse to number
 *
 * @returns {array}   an array of objects sorted descendingly or ascendingly
*/
export const sortingBy = (arr = EMPTY_ARRAY, field, descending = false, stringNumbers = false) => {
  let arrayToSort = [...arr]
  if (stringNumbers && descending) {
    arrayToSort = arrayToSort.map(s => ({ ...s, name: +s.name }))
  }
  if (descending) {
    return arrayToSort.sort((a, b) => a[field] < b[field] ? 1 : -1)
  } else {
    return arrayToSort.sort((a, b) => a[field].toLowerCase() > b[field].toLowerCase() ? 1 : -1)
  }
}

/**
 * Creates an array from the object location separated by '/' key
 * @param   {object} location - location of the object window
 *
 * @returns {array}  an array splitted by '/' key
*/
export const findUrlPathArray = location => {
  return location && location.pathname &&
    encodeURI(location.pathname).replace('%20/%20', '%20slash%20').split('/')
}

/**
 * Pass location string to an object with the active filters
 * @param   {object} location - location of the object window
 * @param   {string} type - landing type
 * @param   {array}  [arr=FILTERS_KEYS_ARR] - array with items to compare with location splitted array
 *
 * @returns {object} object with key value pair of active filters and their values
*/
export const urlPathToObject = (location, type, arr = FILTERS_KEYS_ARR) => {
  const urlPathArray = findUrlPathArray(location) || EMPTY_ARRAY
  const indexToFindFilters = urlPathArray.findIndex(p => TYPES_WITH_FILTER.includes(p)) + 1
  const filtersParams = urlPathArray.slice(indexToFindFilters)
  return filtersParams.reduce((acc, curr, idx) => {
    if (arr.includes(curr) && idx % 2 === 0) {
      // The router component does some decoding, and after encoding again, and decoding again, some extra characters appear
      const value = filtersParams[idx + 1].replace(/%2526/g, '%26').replace(/%2520/g, '%20')
      acc[curr] = decodeURIComponent(value).replace('slash', '/')
    }
    return acc
  }, {})
}

/**
 * Pass the object of active filters to a string location path
 * @param   {object} obj - location of the object window
 * @param   {string} type - landing type
 * @param   {string} [itemToRemove=''] - array with items to compare with location splitted array
 *
 * @returns {string} location path with active filters or empty string if not there filters
*/
export const objectToUrlPath = (obj = EMPTY_OBJECT, type, itemToRemove = '') => {
  return Object.keys(obj).map(item => {
    if (LEVEL_CATEGORIES_TYPES.includes(itemToRemove)) {
      if (item === itemToRemove.split('_')[0]) {
        const splittedCategory = obj[item].split('_')
        splittedCategory.splice(Number(itemToRemove.split('_')[1]) - 1)
        return splittedCategory.length > 0 ? `/${item}/${splittedCategory.join('_')}` : ''
      }
    }
    if (item === itemToRemove) {
      return
    }
    if (Array.isArray(itemToRemove) && itemToRemove.includes(item)) {
      return
    }
    if (item === 'tag') {
      return `/${item}/${encodeURIComponent(obj[item])}`
    }
    return `/${item}/${obj[item]}`
  }).join('').replace(' / ', ' slash ')
}

/**
 * Check if a type is able to handle a search without an input text
 * @param   {string} [type=''] - name of type
 *
 * @returns {boolean} returns true if type is inside of SEARCH_TYPES_WITHOUT_TEXT array
*/
export const isAbleToSearchWithoutText = (type = '') => SEARCH_TYPES_WITHOUT_TEXT.includes(type)

/**
 * Evaluates if query and/or page parameters are valid and format the QueryString
 * @param   {string} query - input search text
 * @param   {number} page - number of current page
 *
 * @returns {string} returns a string to handle query and page params in the QueryString
*/
export const getQueryStrings = (query, page, sort) => {
  let queryStrings = ''
  if (query) {
    if (page && page > 1) {
      queryStrings = `${SEARCH_QUERY}${query}&page=${page}`
    } else {
      queryStrings = `${SEARCH_QUERY}${query}`
    }
  } else if (page && page > 1) {
    queryStrings = `?page=${page}`
  }
  if (sort) {
    queryStrings = queryStrings.length > 0 ? `${queryStrings}&sort=${sort}` : `?sort=${sort}`
  }
  return queryStrings
}

/**
 * Uses URLSearchParams class to get the param 'q' from the location
 * @param   {object} location - location of the object window
 *
 * @returns {string} returns the param 'q' if exist, else empty string
*/
export const getQueryFromUrl = (location = EMPTY_OBJECT) => {
  const params = new URLSearchParams(location.search)
  return params.get('q') || ''
}

/**
 * Uses URLSearchParams class to get the param 'page' from the location
 * @param   {object} location - location of the object window
 *
 * @returns {string} returns the param 'page' if exist, else empty string
*/
export const getPageFromUrl = (location = EMPTY_OBJECT) => {
  const params = new URLSearchParams(location.search)
  return params.get('page') || ''
}

/**
 * Append a component into an element finded by a classname
 * @param   {string} classElement - css class of the element where it is to be appended the component
 * @param   {object} component - jsx component to be appended
 *
 * @returns {object} if elementToAppend const is valid then the append is maded, if not returns the component
*/
export const appendComponent = (classElement, component) => {
  const [elementToAppend] = document.getElementsByClassName(classElement)
  return elementToAppend
    ? ReactDOM.createPortal(component, elementToAppend)
    : component
}

export const isMobileAgent = () => !!MOBILE_AGENTS.filter(uAgent => navigator.userAgent.match(uAgent)).length

export const getInsightsObject = (itemType, itemId, index, gridview, searchType, query) => {
  const insightsCategory = gridview ? `search-${searchType}-grid` : `search-${searchType}-list`
  return {
    category: insightsCategory,
    label: query,
    value: `${itemType}/${itemId}`,
    version: index + 1
  }
}

/**
 * Creating an Event called 'searchResultsLoaded' and dispatching it, should be triggered after search results request
*/
export const dispatchSearchResultsLoadedEvent = detail => {
  const searchResultsLoadedEvent = new CustomEvent('searchResultsLoaded', { detail })
  document.dispatchEvent(searchResultsLoadedEvent)
}

/**
 * Truncate string and add elipsis if necessary
*/
export const truncateText = (text, limit) => text.length <= limit ? text : `${text.slice(0, limit)}...`

export const getProfessionalsDynamicTitle = (filters, activeFilters) => {
  return filters?.[EXP_1].find(f => f.path === activeFilters[EXPERTISE]) || ''
}

export const getProjectsDynamicTitle = (filters, activeFilters) => {
  return filters?.[CATEGORIES].find(f => f.slug === activeFilters[CATEGORIES]) || ''
}

export const getExpertsTitle = (filters, activeFilters) => {
  return filters?.[EXP_1].find(f => f.path === activeFilters) || ''
}

export const showMetaDescriptionAndGetFilterTitle = (type, filtersObject, filters, intl) => {
  if (![PROFESSIONALS, ARTICLE_ARCHIVE].includes(type)) return false

  if (type === ARTICLE_ARCHIVE) {
    if (!filtersObject[TOPIC]) return 'ArchDaily Archive'

    return intl.formatMessage({ ...tagFiltersMessages[filtersObject[TOPIC]] })
  }

  if (filtersObject[EXPERTISE]) {
    const selectedFilter = getExpertsTitle(filters, filtersObject[EXPERTISE])
    if (selectedFilter.slug === 'other') {
      const alternativeName = selectedFilter.path.split('_')
      return alternativeName.slice(0, -1).join(' ')
    }
    return selectedFilter && selectedFilter.name
  } else { return 'Professionals' }
}

/**
 * Shuffle Results
*/
export const shuffleResults = results => {
  if (!results?.length) {
    return []
  }

  for (let i = results.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * i)
    const temp = results[i]
    results[i] = results[j]
    results[j] = temp
  }
  return results
}

/**
 * Abbreviation name
 * @param {string} name - full name
 * @returns {string} nameAbbreviated
 */
export const getNameAbbr = name => {
  const splittedName = name.split(' ') || []
  let nameAbbreviated = splittedName[0][0] || ''
  if (splittedName.length > 1) {
    nameAbbreviated += splittedName[1][0]
  }
  return nameAbbreviated
}

/**
 * Limit the rate at which a function will be executed
 */
export const throttle = (func, limit) => {
  let timeout
  let lastExecution

  return function () {
    const context = this
    const args = arguments
    if (!lastExecution) {
      func.apply(context, args)
      lastExecution = Date.now()
    } else {
      clearTimeout(timeout)
      timeout = setTimeout(function () {
        if (Date.now() - lastExecution > limit) {
          func.apply(context, args)
          lastExecution = Date.now()
        }
      }, limit - (Date.now() - lastExecution))
    }
  }
}

/**
 * Delay execution of a function it stops being called for some time
 * @param { function } func - the function to execute
 * @param { number } wait - the waiting time
 * @returns function
 */
export const debounce = (func, wait) => {
  let timeout

  return (...args) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      func.apply(this, args)
    }, wait)
  }
}

// Trigger function after typing 3 letters
export const handleAsyncQuery = debounce(async (item, searchType, site, query, filters, callback) => {
  if (item === OFFICES) {
    return searchOffices(site, query, filters, callback, searchType === IMAGES)
  }
  return EMPTY_ARRAY
}, 500)

export const appendAltToImages = (images, imagesAlt) => {
  return images.map((image, index) => {
    image['image_alt'] = imagesAlt[index] || ''
    return image
  })
}

export const hideAds = () => {
  const ads = document.querySelectorAll('.js-slot')
  if (!ads) return
  ads.forEach(ad => {
    ad.classList.add('afd-hide')
  })
}

export const executePianoExperience = section => {
  const tp = window.tp || []
  tp.push(['setContentSection', section])
  if (tp.experience) {
    tp.experience.execute()
  }
}
